import * as z from 'zod/mini';
import { UserType } from '@sigma/common/auth/UserType.js';
import { FormInputConfig, FormInputValue } from '@sigma/common/forms/FormInput.js';
import { PatientProfile } from '@sigma/common/patient/PatientProfile.js';
import { Id } from '@sigma/common/schema/Id.js';
import { File } from '@sigma/files-common/File.js';
const eventCommonProps = {
    chatId: Id,
    seq: z.int32(),
    timestamp: z.number(),
    /** UID of the agent step that generated the event. */
    step: z.optional(z.string()),
    /** UID of the substep that generated the event. */
    substep: z.optional(z.string()),
};
export const ChatEventType = z.enum([
    'CHAT_START',
    'PATIENT_MESSAGE',
    'PATIENT_FORM',
    'PATIENT_ANSWER',
    'AGENT_BUSY',
    'AGENT_MESSAGE',
    'AGENT_FORM',
    'AGENT_QUESTION',
    'AGENT_START',
    'AGENT_STEP',
    'AGENT_COMPLETE',
    'AGENT_SUMMARY',
    'AGENT_DONE',
    'STAFF_PROMPT',
    'STAFF_MESSAGE',
    'STAFF_ANSWER',
    'STAFF_JOIN',
    'PROFILE_DATA_REQUEST',
    'PROFILE_DATA_SENT',
    'SUBSCRIPTION_REQUEST',
    'SUBSCRIPTION_START',
    'CHAT_END',
    'SIDE_EFFECTS',
]);
export const ChatBaseEvent = z.object({
    type: z.string(),
    ...eventCommonProps,
});
export const ChatStart = z.object({
    ...eventCommonProps,
    type: z.literal('CHAT_START'),
});
export const ChatPatientMessage = z.object({
    ...eventCommonProps,
    type: z.literal('PATIENT_MESSAGE'),
    content: z.string(),
    files: z.optional(z.array(File)),
});
/** @deprecated */
export const ChatPatientForm = z.object({
    ...eventCommonProps,
    type: z.literal('PATIENT_FORM'),
    inputs: z.array(FormInputValue),
    message: z.nullish(z.string()),
    files: z.optional(z.array(File)),
});
export const ChatPatientAnswer = z.object({
    ...eventCommonProps,
    type: z.literal('PATIENT_ANSWER'),
    questionSeq: z.int32(),
    answer: FormInputValue,
});
export const ChatAgentMessage = z.object({
    ...eventCommonProps,
    type: z.literal('AGENT_MESSAGE'),
    reasoning: z.optional(z.string()),
    message: z.string(),
    target: UserType,
});
/** @deprecated */
export const ChatAgentForm = z.object({
    ...eventCommonProps,
    type: z.literal('AGENT_FORM'),
    reasoning: z.optional(z.string()),
    inputs: z.array(FormInputConfig),
});
export const ChatAgentQuestion = z.object({
    ...eventCommonProps,
    type: z.literal('AGENT_QUESTION'),
    input: FormInputConfig,
    target: UserType,
});
export const ChatAgentSummary = z.object({
    ...eventCommonProps,
    type: z.literal('AGENT_SUMMARY'),
    summary: z.string(),
});
export const ChatAgentDone = z.object({
    ...eventCommonProps,
    type: z.literal('AGENT_DONE'),
    reasoning: z.optional(z.string()),
    message: z.string(),
});
export const ChatAgentBusy = z.object({
    ...eventCommonProps,
    type: z.literal('AGENT_BUSY'),
});
export const ChatAgentStart = z.object({
    ...eventCommonProps,
    type: z.literal('AGENT_START'),
    agentId: Id,
    agentVersion: z.int32(),
});
export const ChatAgentStep = z.object({
    ...eventCommonProps,
    type: z.literal('AGENT_STEP'),
    /** UID of the step being transitioned to. */
    stepId: z.string(),
});
export const ChatAgentComplete = z.object({
    ...eventCommonProps,
    type: z.literal('AGENT_COMPLETE'),
    agentId: Id,
});
export const ChatStaffPrompt = z.object({
    ...eventCommonProps,
    type: z.literal('STAFF_PROMPT'),
    staffId: Id,
    message: z.string(),
});
export const ChatStaffMessage = z.object({
    ...eventCommonProps,
    type: z.literal('STAFF_MESSAGE'),
    staffId: Id,
    message: z.string(),
    files: z.optional(z.array(File)),
});
export const ChatStaffAnswer = z.object({
    ...eventCommonProps,
    type: z.literal('STAFF_ANSWER'),
    questionSeq: z.int32(),
    staffId: Id,
    answer: FormInputValue,
});
export const ChatStaffJoin = z.object({
    ...eventCommonProps,
    type: z.literal('STAFF_JOIN'),
    staffId: Id,
});
export const ChatProfileDataRequest = z.object({
    ...eventCommonProps,
    type: z.literal('PROFILE_DATA_REQUEST'),
});
export const ChatProfileDataSent = z.object({
    ...eventCommonProps,
    type: z.literal('PROFILE_DATA_SENT'),
    profile: PatientProfile,
});
export const ChatSubscriptionRequest = z.object({
    ...eventCommonProps,
    type: z.literal('SUBSCRIPTION_REQUEST'),
    productId: Id,
    subscriptionId: Id,
});
export const ChatSubscriptionStart = z.object({
    ...eventCommonProps,
    type: z.literal('SUBSCRIPTION_START'),
    productId: Id,
    subscriptionId: Id,
});
export const ChatEndEvent = z.object({
    ...eventCommonProps,
    type: z.literal('CHAT_END'),
    agentId: Id,
});
export const ChatSideEffects = z.object({
    ...eventCommonProps,
    type: z.literal('SIDE_EFFECTS'),
    /** seq of the event that produced these effects (e.g. the `PATIENT_ANSWER`, or the file message). */
    sourceSeq: z.int32(),
    /** Created observation ids, incl. any derived BMI. Present only when observations were created. */
    observationIds: z.optional(z.array(Id)),
    /** Created patient-document id. Present only for document (file-parsing) side effects. */
    documentId: z.optional(Id),
    /** Source file ids that composed {@link documentId}. Present only for document side effects. */
    fileIds: z.optional(z.array(Id)),
});
export const ChatEvent = z.discriminatedUnion('type', [
    ChatStart,
    ChatPatientMessage,
    ChatPatientForm,
    ChatPatientAnswer,
    ChatAgentBusy,
    ChatAgentStart,
    ChatAgentStep,
    ChatAgentComplete,
    ChatAgentMessage,
    ChatAgentForm,
    ChatAgentSummary,
    ChatAgentQuestion,
    ChatAgentDone,
    ChatStaffPrompt,
    ChatStaffMessage,
    ChatStaffAnswer,
    ChatStaffJoin,
    ChatProfileDataRequest,
    ChatProfileDataSent,
    ChatSubscriptionRequest,
    ChatSubscriptionStart,
    ChatEndEvent,
    ChatSideEffects,
]);
export function parseChatEvent(value) {
    return z.parse(ChatEvent, value);
}
//# sourceMappingURL=ChatEvent.js.map